import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const AuthModal = ({ isOpen, onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setName(""); setEmail(""); setPassword(""); setError(""); };
  const switchMode = () => { setMode(mode === "login" ? "register" : "login"); reset(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
        await register(name, email, password);
      }
      reset();
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="auth-modal-overlay"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl relative"
            data-testid="auth-modal"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-sw-text-muted hover:text-sw-text transition-colors" data-testid="auth-modal-close">
              <X size={20} />
            </button>

            <div className="text-center mb-6">
              <h2 className="text-xl font-heading font-700 text-sw-text" data-testid="auth-modal-title">
                {mode === "login" ? "Welcome Back" : "Join Swadishes"}
              </h2>
              <p className="text-sm text-sw-text-muted mt-1">
                {mode === "login" ? "Sign in to your kitchen" : "Create your personalized kitchen"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <label className="text-xs font-medium text-sw-text-secondary mb-1.5 block">Full Name</label>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your name" data-testid="auth-name-input"
                    className="w-full bg-cream-secondary/50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-sw-text placeholder:text-sw-text-muted/50 focus:outline-none focus:border-earth-green focus:ring-1 focus:ring-earth-green/20 transition-all"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-sw-text-secondary mb-1.5 block">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com" required data-testid="auth-email-input"
                  className="w-full bg-cream-secondary/50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-sw-text placeholder:text-sw-text-muted/50 focus:outline-none focus:border-earth-green focus:ring-1 focus:ring-earth-green/20 transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-sw-text-secondary mb-1.5 block">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters" required minLength={6} data-testid="auth-password-input"
                    className="w-full bg-cream-secondary/50 border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm text-sw-text placeholder:text-sw-text-muted/50 focus:outline-none focus:border-earth-green focus:ring-1 focus:ring-earth-green/20 transition-all"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-sw-text-muted" data-testid="auth-toggle-password">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2" data-testid="auth-error">{error}</p>
              )}

              <button
                type="submit" disabled={loading}
                className="w-full bg-earth-green text-white rounded-full py-3 font-medium text-sm hover:bg-earth-green-dark transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="auth-submit-btn"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {mode === "login" ? "Sign In" : "Create Account"}
              </button>
            </form>

            <p className="text-center text-xs text-sw-text-muted mt-5">
              {mode === "login" ? "New to Swadishes?" : "Already have an account?"}{" "}
              <button onClick={switchMode} className="text-earth-green font-medium hover:underline" data-testid="auth-switch-mode">
                {mode === "login" ? "Create account" : "Sign in"}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
